import React, { useCallback, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useConfig } from "../../hooks/useConfig"
import { useParticipant } from "../../hooks/useParticipant"
import { useBoothSession } from "../../hooks/useBoothSession"
import CameraErrorFallbackView from "../views/CameraErrorFallbackView"
import CropperModal from "../modals/CropperModal"
import SwitchDeviceModal from "../modals/SwitchDeviceModal"
import SizeRejectionModal from "../modals/SizeRejectionModal"
import { checkImageSize, cropImage, processImageFile } from "../../utils/ImageUtil"
import { Area } from "react-easy-crop/types"

function CameraErrorFallbackPage() {
  const navigate = useNavigate()

  const config = useConfig()
  const { participant } = useParticipant(config.presetCode)
  const { rawImage, setRawImage } = useBoothSession()

  const [showSwitchDevice, setShowSwitchDevice] = useState(false)
  const [showSizeRejection, setShowSizeRejection] = useState(false)
  const [imageToCrop, setImageToCrop] = useState<string | null>(null)

  const sharedCanvas = useRef<HTMLCanvasElement>(null)

  const handleCapturedImage = useCallback(
    (image: string) => {
      checkImageSize(image, config.headshots.minimumSize, (acceptable) => {
        if (acceptable) {
          setRawImage(image)
          setImageToCrop(null)
          if (config.aiEffectOn) {
            const guestInputs = config.aiEffectSequence?._embedded.guestInputs ?? []
            if (guestInputs.length > 0) {
              navigate(`/${config.presetCode}/guest-input`)
            } else {
              navigate(`/${config.presetCode}/photo-processing`)
            }
          } else if (config.backgroundReplacementOn) {
            navigate(`/${config.presetCode}/photo-processing`)
          } else {
            navigate(`/${config.presetCode}/edit`)
          }
        } else {
          setShowSizeRejection(true)
          setRawImage(null)
        }
      })
    },
    [config, navigate, setRawImage],
  )

  const showImageCropper = useCallback(
    (imageFile: File) => {
      processImageFile(imageFile, (imageData: string) => {
        checkImageSize(imageData, config.headshots.minimumSize, (acceptable) => {
          if (acceptable) {
            setImageToCrop(imageData)
          } else {
            setShowSizeRejection(true)
          }
        })
      })
    },
    [config],
  )

  const cancelImageCrop = useCallback(() => {
    setImageToCrop(null)
  }, [])

  const finishCrop = useCallback(
    (cropRect: Area, imageData: string) => {
      const image = new Image()
      image.src = imageData

      const canvas = sharedCanvas.current
      if (!canvas) {
        throw new Error("Canvas missing")
      }

      handleCapturedImage(cropImage(image, canvas, cropRect))
    },
    [handleCapturedImage],
  )

  const theme = config.theme

  return (
    <>
      <canvas
        className="hiddenCanvas"
        ref={sharedCanvas}
      ></canvas>

      {imageToCrop && (
        <CropperModal
          show={rawImage == null}
          showFaceGuide={config.headshots.showFaceGuide}
          onClose={cancelImageCrop}
          image={imageToCrop}
          finishCrop={finishCrop}
        />
      )}

      <SwitchDeviceModal
        show={showSwitchDevice}
        participant={participant}
        onHide={() => setShowSwitchDevice(false)}
      />

      {showSizeRejection && (
        <SizeRejectionModal
          show={showSizeRejection}
          minWidth={config.headshots.minimumSize.width}
          minHeight={config.headshots.minimumSize.height}
          onHide={() => setShowSizeRejection(false)}
        />
      )}

      <CameraErrorFallbackView
        theme={theme}
        handleSwitchDevice={() => setShowSwitchDevice(true)}
        handleUserSelectedImage={(imageFile) => showImageCropper(imageFile)}
      />
    </>
  )
}

export default CameraErrorFallbackPage
